import { useEffect, useRef } from "react";
import { useTracks } from "@livekit/components-react";
import { Track } from "livekit-client";
import { registerVideoElement, unregisterVideoElement } from "../../lib/videoTokenRegistry";

// Renders nothing — silently keeps a hidden <video> element attached & up to date for every
// camera track currently published in the room (local + remote), so any player's live video
// can be used as their map token image via lib/videoTokenRegistry.
export default function VideoTokenBridge() {
  const trackRefs = useTracks([Track.Source.Camera]);
  const attachedRef = useRef(new Map()); // identity -> { track, videoEl }

  useEffect(() => {
    const attached = attachedRef.current;
    const seen = new Set();
    trackRefs.forEach((ref) => {
      const track = ref.publication?.track;
      const identity = ref.participant?.identity;
      if (!track || !identity) return;
      seen.add(identity);
      const existing = attached.get(identity);
      if (existing && existing.track === track) return;
      if (existing) existing.track.detach(existing.videoEl);
      const videoEl = document.createElement("video");
      videoEl.autoplay = true;
      videoEl.muted = true;
      videoEl.playsInline = true;
      track.attach(videoEl);
      attached.set(identity, { track, videoEl });
      registerVideoElement(identity, videoEl);
    });
    for (const [identity, entry] of [...attached.entries()]) {
      if (!seen.has(identity)) {
        entry.track.detach(entry.videoEl);
        unregisterVideoElement(identity);
        attached.delete(identity);
      }
    }
  }, [trackRefs]);

  useEffect(() => {
    const attached = attachedRef.current;
    return () => {
      for (const [identity, entry] of attached.entries()) {
        entry.track.detach(entry.videoEl);
        unregisterVideoElement(identity);
      }
      attached.clear();
    };
  }, []);

  return null;
}
